import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { api, type LadderTop } from '../api/client';

export function Ladder() {
  const { t } = useTranslation();
  const me = usePlayerStore(s => s.me);
  const [top, setTop] = useState<LadderTop | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    void api.ladder.top()
      .then(data => setTop(data))
      .catch(e => setLoadError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (!me) return <Navigate to="/login" replace />;

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="cyan">{t('ladder.title')}</PixelText>
      </div>

      {/* Player stats */}
      <PixelPanel>
        <PixelText size="sm" color="cyan">{t('ladder.your_tier', { tier: me.tier })}</PixelText>
        <PixelText size="sm" color="yellow" className="block mt-1">{t('ladder.lp', { pts: me.ladderPts })}</PixelText>
      </PixelPanel>

      {/* Top leaderboard */}
      <PixelPanel>
        <PixelText size="base" color="pink" className="mb-2 block">{t('ladder.top_players')}</PixelText>
        {loading && <PixelText size="sm" color="white">{t('ladder.loading')}</PixelText>}
        {loadError && (
          <PixelText size="sm" color="yellow">
            {t('ladder.load_error', { error: loadError })}
          </PixelText>
        )}
        {top && top.entries.length === 0 && (
          <PixelText size="sm" color="cyan">
            {t('ladder.no_players')}
          </PixelText>
        )}
        {top && top.entries.map(entry => (
          <div key={entry.playerId} className="flex justify-between items-center py-1 border-b border-ink-700">
            <PixelText size="sm" color="yellow">#{entry.rank}</PixelText>
            <PixelText size="sm" color="white">{entry.displayName}</PixelText>
            <PixelText size="sm" color="cyan">{entry.tier}</PixelText>
            <PixelText size="sm" color="green">{entry.pts} LP</PixelText>
          </div>
        ))}
      </PixelPanel>

      <Link to="/ladder/play">
        <PixelButton className="w-full">{t('ladder.battle')}</PixelButton>
      </Link>
    </main>
  );
}
